import { useTranslation } from 'react-i18next';
import { useCopyToClipboard } from 'usehooks-ts';
import { Copy } from 'lucide-react';

import { Modal, type ModalProps } from '@app/shared/ui/organisms/Common';
import { SettlementBackground, SettlementImage } from '@app/shared/ui/molecules/Home';
import { Button, Heading } from '@app/shared/ui/atoms/Common';
import notification from '@app/shared/utils/notification';
import { telegramVibrate } from '@app/shared/utils/common';
import { INITIAL_REWARD } from '@app/shared/constants/common';
import { twoPandasFriendsImage, cookieImage } from '@app/assets';

interface InviteFriendsModalProps extends Pick<ModalProps, 'isOpen' | 'onOpenChange'> {
  referralLink: string;
  modalTextTranslationKey?: string;
}

export function InviteFriendsModal({
  isOpen,
  onOpenChange,
  referralLink,
  modalTextTranslationKey,
}: InviteFriendsModalProps) {
  const { t } = useTranslation();
  const [, copyToClipboard] = useCopyToClipboard();

  const handleCopyToClipboard = async () => {
    telegramVibrate();

    try {
      await copyToClipboard(referralLink);
      notification.success(t('common.copied'));
    } catch {
      notification.error(t('errors.general'));
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      contentClassName="bg-[#0A78E6BF]"
      hiddenTitle="Invite friends"
    >
      <div className="flex flex-col gap-5 overflow-x-hidden">
        <div className="relative mx-auto mt-1 size-[105px]">
          <SettlementBackground />
          <SettlementImage imageUrl={twoPandasFriendsImage} name="pandas" className="size-full" />
        </div>

        {modalTextTranslationKey ? (
          <Heading level={2} className="flex items-center justify-center gap-1.5 font-bold">
            {t(modalTextTranslationKey)}
          </Heading>
        ) : (
          <div className="flex flex-col">
            <Heading level={2} className="flex items-center justify-center gap-1.5 font-bold">
              {`${t('home.invite_friends_modal.get')} +${INITIAL_REWARD}`}
              <img src={cookieImage} alt="cookie" className="w-5" />
            </Heading>
            <Heading level={2} className="text-center font-bold">
              {t('home.invite_friends_modal.for_invitation')}
            </Heading>
          </div>
        )}

        <Button
          variant="custom"
          shadow="blue"
          className="flex items-center overflow-hidden bg-[#FFFFFF70]"
          onClick={handleCopyToClipboard}
        >
          <div className="flex-1 truncate px-4 text-center text-base font-normal">
            {referralLink}
          </div>
          <div className="flex h-full items-center justify-center bg-[#0B87F9] px-3.5">
            <Copy className="w-5" />
          </div>
        </Button>
      </div>
    </Modal>
  );
}
